import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  Signal,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  equals,
  isEmptyObject,
  isEmptyObjectOrHasEmptyValues,
  isNotEmpty,
  isPresent,
  PreciseNumber,
  toBoolean,
  unique,
  unsubscribe,
} from '@fcom/core/utils';
import { finShare, snapshot } from '@fcom/rx';
import {
  GroupedServices,
  NO_VARIANTS,
  SelectionsPerTraveler,
  ServiceCatalogService,
  ServicePassenger,
  ServicesPerTraveler,
  TravelerService,
} from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairAmount,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceItem,
  FinnairServiceVariantItemV3,
  PaxLevelQuota,
  SubCategory,
} from '@fcom/dapi/api/models';
import {
  AspectRatios,
  ButtonMode,
  ButtonTheme,
  ChipTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationTheme,
  TagTheme,
} from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { getLoungeContentFragmentKey, NON_SCHENGEN_LOUNGES } from '@fcom/booking/utils/common-data.utils';
import { MediaQueryService, ToasterService } from '@fcom/common/services';
import { LanguageService } from '@fcom/ui-translate';
import { safeSelect } from '@fcom/core/selectors/selector-utils';

import {
  flightsAndPaxWhichShouldHaveSeatsClearedIfAddingPetcSelector,
  passengersShouldBeShownPetcBlockingMessageSelector,
  passengersShouldBeShownPetcConfirmationModalSelector,
} from './common-product-selection.component.selector';
import {
  calculatePassengerCountQuota,
  CATEGORY_QUOTA_COPY,
  countOwnUnitsOfVariants,
  findServiceItemsForPassengerForFragment,
  getCategoryQuotaNotification,
  getCategoryQuotaState,
  getFilteredTravelers,
  getPassengerTierLevel,
  getServiceOverlapStateForPaxForFlight,
  getServicesNotifications,
  hasTravelerService,
  isKnownQuota,
  isLowQuota,
  paidUnitsOfVariants,
  QuotaNotification,
  quotaBearingVariantsOf,
  VariantSelectionValues,
  VariantUnits,
} from '../../utils';
import {
  overlappingSelectionsForCategorySelector,
  QuantityForFragmentAndPassenger,
  SeatMapActions,
  ServicesActions,
  serviceSelectionsForFragment,
  serviceSelectionsForFragmentAndTraveler,
} from '../../../../store';
import {
  ConfirmModal,
  ConfirmModalData,
  EligibilitiesForFragments,
  ExtendedService,
  Notification,
  ServiceOverlapResolution,
  ProductInputType,
  ProductTierPrice,
  PassengerCountQuotaType,
  ServiceOverlaps,
} from '../../interfaces';
import { TabLabelComponent } from '../../../tabs';
import { asTravelerService } from '../../../../store/utils/services.utils';
import { asSegmentId, getGroupedServices } from '../../../../utils/common-booking.utils';
import { accumulateTieredPrice } from '../../../../utils/amount.utils';

export enum SelectionState {
  LOADING,
  HAS_SERVICES,
  NO_SERVICES,
}

export interface ConfirmModalTranslationKeys {
  title: string;
  firstContentParagraph: string;
  secondContentParagraph: string;
  closeButton: string;
  continueButton: string;
}

interface TravelerServices {
  traveler: ServicePassenger;
  services: ExtendedService[];
  selectedServices$: Observable<ExtendedService[]>;
  hasServiceSelected$: Observable<boolean>;
  additionalInfoBlockUrl: string | undefined;
  additionalInfoBlockNotification: string | undefined;
  filters: SubCategoryFilter[];
  selectedFilter$: BehaviorSubject<SubCategoryFilter>;
  combinedGroupedServices: GroupedServices[];
  noneLeftForTraveler$: Observable<boolean>;
}

interface ProductConfig {
  notSelectedVariant?: string;
  toppedQuotaKey?: string;
  quotaLimitKey?: string;
  noQuotaLeftKey?: string;
  blockSelectionNotification?: string;
  alreadyPurchased: string;
}

const CONFIG: Record<string, ProductConfig> = {
  [Category.WIFI]: {
    notSelectedVariant: '',
    alreadyPurchased: 'ancillaries.wifi.alreadyPurchased',
  },
  [Category.MEAL]: {
    notSelectedVariant: '',
    alreadyPurchased: 'ancillaries.meals.alreadyPurchased',
  },
  [Category.LOUNGE]: {
    alreadyPurchased: 'ancillaries.lounge.alreadyPurchased',
  },
  [Category.SPORT]: {
    alreadyPurchased: 'ancillaries.sport.alreadyPurchased',
  },
  [Category.CABIN_BAGGAGE]: {
    alreadyPurchased: 'ancillaries.cabinBaggage.alreadyPurchased',
  },
  [Category.BAGGAGE]: {
    alreadyPurchased: 'ancillaries.bags.alreadyPurchased',
  },
  [Category.PET]: {
    quotaLimitKey: 'ancillaries.pet.quota.quotaLimit',
    toppedQuotaKey: 'ancillaries.pet.quota.toppedQuota',
    noQuotaLeftKey: 'ancillaries.pet.quota.noQuotaLeft',
    blockSelectionNotification: 'ancillaries.pet.hasIncompatibleSeat',
    alreadyPurchased: 'ancillaries.pet.alreadyPurchased',
  },
  [Category.PRIORITY]: {
    alreadyPurchased: 'ancillaries.priority.alreadyPurchased',
  },
};

type SelectionWithCategoryAndFragment = [
  SelectionsPerTraveler<TravelerService[]>,
  Category,
  FinnairItineraryItemFlight | FinnairBoundItem,
];

type TravelerSelections = VariantSelectionValues;
type TravelerForm = FormGroup<Record<string, FormControl<boolean | string | number>>>;

type SubCategoryFilter = SubCategory | 'all' | 'none';

const COMMON_TOASTER_ID = 'COMMON-TOASTER-ID';

@Component({
  selector: 'fin-common-product-selection',
  templateUrl: 'common-product-selection.component.html',
  styleUrls: ['common-product-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommonProductSelectionComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<BookingAppState>>(Store);
  private fb = inject(FormBuilder);
  private mediaQueryService = inject(MediaQueryService);
  private languageService = inject(LanguageService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  private elementRef = inject(ElementRef);

  private tabLabels = viewChildren(TabLabelComponent);

  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly CATEGORY_QUOTA_COPY = CATEGORY_QUOTA_COPY;
  readonly CONFIG = CONFIG;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly PassengerCountQuotaType = PassengerCountQuotaType;
  readonly SelectionState = SelectionState;
  readonly SubCategory = SubCategory;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;
  readonly isLowQuota = isLowQuota;
  readonly ChipTheme = ChipTheme;

  @Input()
  servicesReady$: Observable<boolean>;

  @Input()
  category$: Observable<Category>;

  @Input()
  flightOrBound$: Observable<FinnairItineraryItemFlight | FinnairBoundItem>;

  @Input()
  services$: Observable<ServicesPerTraveler>;

  @Input()
  includedServices$: Observable<FinnairServiceItem | null> = of(null);

  @Input()
  combinedServices$: Observable<FinnairServiceItem | null> = of(null);

  travelers = input.required<FinnairPassengerItem[]>();
  filteredTravelers: Signal<ServicePassenger[]> = computed(() => getFilteredTravelers(this.travelers()));

  @Input()
  initialSelectedTravelerId$: Observable<string> = of(undefined);

  @Input()
  confirmModalData$: BehaviorSubject<ConfirmModal | undefined> = new BehaviorSubject<ConfirmModal>({ open: false });

  @Input()
  availability$: Observable<EligibilitiesForFragments> = of({});

  @Input()
  serviceQuantityMap$: Observable<QuantityForFragmentAndPassenger> = of({});

  @Input()
  paxLevelQuota$: Observable<PaxLevelQuota> = of({ combinedQuota: 0, variants: [] });

  @Input()
  remainingVariantQuotas$: Observable<Record<string, VariantUnits>> = of({});

  @Input()
  remainingCategoryQuotas$: Observable<Record<string, number>> = of({});

  @Input()
  isPersonalizedMealOffer?: boolean = false;

  @Input()
  isHighlightedServicesAvailable = false;

  @Output()
  openConfirmModalWithData: EventEmitter<ConfirmModalData> = new EventEmitter<ConfirmModalData>();

  form: FormGroup<Record<string, TravelerForm>>;
  state$: Observable<SelectionState>;
  additionalText: string | undefined;
  travelersAndServices$: Observable<TravelerServices[]>;
  travelerToServiceVariantTierPricesMap$: Observable<Map<string, Map<string, ProductTierPrice>>>;
  travelerProductQuantityChange$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  initialTravelerIdSelected$: Observable<string>;
  serviceTabGroupId: string;
  notifications$: Observable<Notification[]>;
  showSportNotification$: Observable<boolean>;
  blockingNotifications$: Observable<Notification[]>;
  aspectRatio$: Observable<keyof typeof AspectRatios>;
  isTabletDown$: Observable<boolean>;
  category: Category;
  serviceOverlapsByPax!: Signal<Record<string, ServiceOverlaps>>;
  categoryQuotaNotification$: Observable<QuotaNotification | undefined>;
  passengersThatAreBlockedFromSelecting$: Observable<string[]>;
  passengersThatShouldConfirmSelection$: Observable<string[]>;

  private overlapResolutions: WritableSignal<Record<string, ServiceOverlapResolution>> = signal({});
  private subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.form = new FormGroup({});

    this.isTabletDown$ = this.mediaQueryService.isBreakpoint$('tablet_down');
    this.aspectRatio$ = this.isTabletDown$.pipe(map((isTabletDown) => (isTabletDown ? '21x9' : '1x1')));

    this.passengersThatAreBlockedFromSelecting$ = combineLatest([
      this.category$,
      this.flightOrBound$,
      this.store$.select(passengersShouldBeShownPetcBlockingMessageSelector),
    ]).pipe(
      map(
        ([category, flightOrBound, blockedPaxByBound]) =>
          (category === Category.PET && blockedPaxByBound[flightOrBound.id]) || []
      )
    );

    this.passengersThatShouldConfirmSelection$ = combineLatest([
      this.category$,
      this.flightOrBound$,
      this.store$.select(passengersShouldBeShownPetcConfirmationModalSelector),
    ]).pipe(
      map(
        ([category, flightOrBound, blockedPaxByBound]) =>
          (category === Category.PET && blockedPaxByBound[flightOrBound.id]) || []
      )
    );

    const baseStream$ = combineLatest([this.category$, this.flightOrBound$]).pipe(
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1].id === b[1].id)
    );
    this.subscriptions.add(
      baseStream$.subscribe(([category, flightOrBound]) => {
        this.category = category;
        this.serviceTabGroupId = `${category}-${flightOrBound.id}-travelers`;
        this.additionalText = this.getAdditionalText(category, flightOrBound);
        this.cdr.markForCheck();
      })
    );

    // Bridge the filteredTravelers signal into the observable world once, since it is combined with the
    // still-observable category$/flightOrBound$/services$ inputs below. filter(isNotEmpty) waits for the
    // passenger list to load before setting up the form and traveler services.
    const filteredTravelers$: Observable<ServicePassenger[]> = toObservable(this.filteredTravelers, {
      injector: this.injector,
    }).pipe(filter(isNotEmpty), finShare());

    this.setupForm(baseStream$, filteredTravelers$);
    this.setupTravelersAndServices(baseStream$, filteredTravelers$);

    this.state$ = combineLatest([
      this.servicesReady$.pipe(startWith(false)),
      this.travelersAndServices$.pipe(map(isNotEmpty), startWith(false)),
    ]).pipe(
      map(([servicesReady, hasServices]: [boolean, boolean]) => {
        if (!servicesReady) {
          return SelectionState.LOADING;
        }
        if (hasServices) {
          return SelectionState.HAS_SERVICES;
        }
        return SelectionState.NO_SERVICES;
      }),
      distinctUntilChanged(),
      finShare()
    );

    // == Tabs ==
    this.initialTravelerIdSelected$ = combineLatest([this.travelersAndServices$, this.initialSelectedTravelerId$]).pipe(
      filter(([travelersWithServices]) => travelersWithServices.length > 0), // wait for data to load
      map(([travelersWithServices, initialTravelerId]) => initialTravelerId ?? travelersWithServices[0]?.traveler.id),
      take(1) // emit only once to prevent resetting active tab back to first passenger
    );

    const flightOrBound = toSignal(this.flightOrBound$, { injector: this.injector });
    const category = toSignal(this.category$, { injector: this.injector });
    const serviceOverlapsForCategory = computed(() =>
      category() ? this.store$.selectSignal(overlappingSelectionsForCategorySelector(category()))() : {}
    );

    this.serviceOverlapsByPax = computed(() => {
      const currentFlightOrBound = flightOrBound();
      if (!currentFlightOrBound) {
        return {};
      }

      const resolutions = this.overlapResolutions();
      const fragmentId = asSegmentId(currentFlightOrBound.id);

      return this.travelers().reduce<Record<string, ServiceOverlaps>>((perTraveler, traveler) => {
        const resolution = resolutions[traveler.id];

        perTraveler[traveler.id] = getServiceOverlapStateForPaxForFlight(
          serviceOverlapsForCategory(),
          fragmentId,
          traveler.id,
          resolution
        );

        return perTraveler;
      }, {});
    });

    this.notifications$ = combineLatest([this.services$, this.availability$, this.category$, this.flightOrBound$]).pipe(
      map(([services, availability, category, flightOrBound]) =>
        getServicesNotifications(services, availability, category, flightOrBound)
      ),
      finShare()
    );

    this.blockingNotifications$ = this.notifications$.pipe(
      map((notifications) => notifications.filter((n) => n.blockSelection))
    );

    this.showSportNotification$ = this.category$.pipe(
      map((category) => {
        return category === Category.BAGGAGE;
      })
    );
  }

  ngOnDestroy(): void {
    this.toasterService.removeMessage(COMMON_TOASTER_ID);
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  clickInput(event: Event, service: ExtendedService, flightOrBoundId: string, travelerId: string): void {
    this.subscriptions.add(
      this.passengersThatShouldConfirmSelection$.pipe(take(1)).subscribe((confirmRequiredPax) => {
        if (confirmRequiredPax.includes(travelerId)) {
          event.preventDefault();
          this.openConfirmModalWithData.emit({
            variant: service.variant,
            flightOrBoundId: flightOrBoundId,
            travelerId: travelerId,
          });
        }
      })
    );
  }

  dismissOverlapNotificationForPax(travelerId: string): void {
    this.overlapResolutions.update((resolutions) => ({ ...resolutions, [travelerId]: 'discard' }));

    // The notification unmounts and the products become selectable in the same render, so the control the
    // passenger asked to continue to only becomes focusable once that render has happened.
    afterNextRender(() => this.focusFirstSelectableProduct(travelerId), { injector: this.injector });
  }

  keepExistingSelectionForPax(travelerId: string): void {
    this.focusTravelerTab(travelerId);
    this.overlapResolutions.update((resolutions) => ({ ...resolutions, [travelerId]: 'keep' }));
  }

  addQuantity(service: ExtendedService, flightOrBoundId: string, travelerId: string): void {
    const currentValue = +this.form.controls[travelerId]?.controls[service.variant].value;
    const quantity = currentValue + 1;

    this.subscriptions.add(
      this.passengersThatShouldConfirmSelection$.pipe(take(1)).subscribe((confirmRequiredPax) => {
        if (confirmRequiredPax.includes(travelerId)) {
          this.openConfirmModalWithData.emit({
            variant: service.variant,
            quantity,
            flightOrBoundId: flightOrBoundId,
            travelerId: travelerId,
          });
        } else {
          this.form.controls[travelerId]?.controls[service.variant]?.setValue(quantity);
        }
        this.travelerProductQuantityChange$.next(true);
      })
    );
  }

  removeQuantity(service: ExtendedService, travelerId: string): void {
    const currentValue = +this.form.controls[travelerId]?.controls[service.variant].value;
    const quantity = currentValue - 1;

    this.form.controls[travelerId]?.controls[service.variant]?.setValue(quantity);
    this.travelerProductQuantityChange$.next(true);
  }

  /**
   * Focuses the first product the passenger can still select, falling back to their tab when the panel has
   * nothing selectable (e.g. quota reached, or a quantity based product with no focusable input).
   */
  private focusFirstSelectableProduct(travelerId: string): void {
    const selectableProduct: HTMLElement | null = this.elementRef.nativeElement.querySelector(
      `[id="tab-${travelerId}-content"] input[type="radio"]:not([disabled]), ` +
        `[id="tab-${travelerId}-content"] input[type="checkbox"]:not([disabled])`
    );

    if (selectableProduct) {
      selectableProduct.focus();
    } else {
      this.focusTravelerTab(travelerId);
    }
  }

  private focusTravelerTab(travelerId: string): void {
    this.tabLabels()
      .find((tabLabel) => tabLabel.tabId === travelerId)
      ?.focus();
  }

  private getAdditionalInfoBlock(
    services: ExtendedService[],
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem
  ): string | undefined {
    if (category === Category.LOUNGE) {
      return getLoungeContentFragmentKey(services.some((service) => NON_SCHENGEN_LOUNGES.includes(service.variant)));
    }
    if (category === Category.PRIORITY) {
      return this.departsFromHelsinki(flightOrBound)
        ? 'fragments.helsinkiPriorityContent.url'
        : 'fragments.nonHelsinkiPriorityContent.url';
    }
    return `fragments.services.productInfo.${category}.url`;
  }

  private getAdditionalInfoBlockNotification(
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem
  ): string | undefined {
    if (category === Category.PRIORITY && this.departsFromHelsinki(flightOrBound)) {
      return 'ancillaries.priority.additionalInfoBlockNotificationHel';
    }
    return undefined;
  }

  private getAdditionalText(
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem
  ): string | undefined {
    if (category === Category.WIFI) {
      if ((flightOrBound as FinnairItineraryItemFlight).isShortHaul) {
        return 'ancillaries.wifi.shorthaulAdditionalInformation';
      }
      return 'ancillaries.wifi.additionalInformation';
    }
    return undefined;
  }

  private addValuesToFormAfterConfirmModal(confirmData: ConfirmModalData) {
    const formControls = this.form.controls[confirmData.travelerId].controls;
    if (formControls['radio']) {
      formControls['radio'].setValue(confirmData.variant);
    } else if (confirmData.quantity) {
      formControls[confirmData.variant].setValue(confirmData.quantity);
    } else {
      formControls[confirmData.variant].setValue(true);
    }
  }

  private setupForm(
    baseStream$: Observable<[Category, FinnairItineraryItemFlight | FinnairBoundItem]>,
    filteredTravelers$: Observable<ServicePassenger[]>
  ): void {
    this.subscriptions.add(
      this.confirmModalData$
        .pipe(
          withLatestFrom(this.flightOrBound$),
          filter(
            ([data, flightOrBound]) =>
              (data.confirmData?.variant || data.confirmData?.removeSelection) &&
              data.confirmData?.travelerId &&
              flightOrBound.id === data.confirmData?.flightOrBoundId &&
              !data.open
          )
        )
        .subscribe(([{ confirmData }]) =>
          confirmData.removeSelection
            ? this.removeSelections(confirmData.travelerId, confirmData.flightOrBoundId)
            : this.addValuesToFormAfterConfirmModal(confirmData)
        )
    );

    this.subscriptions.add(
      baseStream$
        .pipe(
          switchMap(([category, flightOrBound]) => {
            return combineLatest([
              of(category),
              of(flightOrBound),
              this.store$.pipe(
                serviceSelectionsForFragment<TravelerService | TravelerService[]>(flightOrBound.id, category)
              ),
              filteredTravelers$.pipe(
                map((travelers) => travelers.map((traveler) => traveler.id)),
                distinctUntilChanged()
              ),
              this.services$,
              this.includedServices$,
            ]).pipe(take(1));
          })
        )
        .subscribe(([category, flightOrBound, initialSelections, travelerIds, services, includedServices]) => {
          travelerIds.forEach((travelerId: string) => {
            this.form.removeControl(travelerId, { emitEvent: false });
            const initialServices: TravelerService[] = [].concat(initialSelections?.[travelerId] ?? []);
            const travelerForm: TravelerForm = this.fb.group({});

            if (this.isRadioDisplay()) {
              const includedServicesForTravelerAndFragment = findServiceItemsForPassengerForFragment(
                this.category,
                includedServices,
                travelerId,
                flightOrBound.id
              );

              travelerForm.registerControl(
                'radio',
                this.fb.control(
                  includedServicesForTravelerAndFragment?.[0]?.variant ||
                    initialServices?.[0]?.variant ||
                    CONFIG[category]?.notSelectedVariant
                )
              );
            } else {
              const allServices = services?.[travelerId]?.filter((s) => s.isService) ?? [];
              allServices.forEach((is) => {
                const initialSelection = initialServices.find((s) => s.variant === is.variant);
                const initialQuantityOrBoolean =
                  is.maxNumberItems > 1 ? (initialSelection?.quantity ?? 0) : !!initialSelection;
                travelerForm.registerControl(is.variant, this.fb.control(initialQuantityOrBoolean));
              });
            }
            this.form.registerControl(travelerId, travelerForm);
          });
        })
    );

    const selections$ = this.form.valueChanges.pipe(
      filter((model) => !isEmptyObjectOrHasEmptyValues(model)),
      withLatestFrom(this.services$),
      map(([model, services]) => {
        return Object.keys(model).reduce((selections: SelectionsPerTraveler<TravelerService[]>, travelerId: string) => {
          const servicesForTraveler = services[travelerId] ?? [];

          const travelerSelections = servicesForTraveler
            .filter((sm: ServiceCatalogService) => {
              if (sm.maxNumberItems > 1) {
                return Number(model[travelerId][sm.variant]) > 0;
              }
              return toBoolean(model[travelerId][sm.variant]) || model[travelerId]['radio'] === sm.variant;
            })
            .map((s) => {
              if (!s.isService) {
                return undefined;
              }
              const quantity = model[travelerId][s.variant];
              return asTravelerService(s, typeof quantity === 'number' ? quantity : 1);
            });

          selections[travelerId] = travelerSelections;
          return selections;
        }, {});
      })
    );

    this.subscriptions.add(
      baseStream$
        .pipe(
          switchMap(([category, flightOrBound]) =>
            selections$.pipe(
              map((selections) => [selections, category, flightOrBound] as SelectionWithCategoryAndFragment)
            )
          ),
          withLatestFrom(
            this.includedServices$,
            this.store$.select(flightsAndPaxWhichShouldHaveSeatsClearedIfAddingPetcSelector)
          )
        )
        .subscribe(([[selections, category, flightOrBound], includedServices, petcIncompatibility]) => {
          const selectionsToStore = this.isRadioDisplay(category)
            ? Object.entries(selections).reduce((selectionsPerTraveler, [traveledId, selectionsForFragment]) => {
                const servicesForFragments = findServiceItemsForPassengerForFragment(
                  category,
                  includedServices,
                  traveledId,
                  flightOrBound.id
                );

                selectionsPerTraveler[traveledId] = selectionsForFragment.filter((selection) => {
                  return !servicesForFragments.some(
                    ({ variant, modifiable }) => !modifiable && variant === selection.variant
                  );
                });

                return selectionsPerTraveler;
              }, {})
            : selections;

          this.checkAndQueueToasterNotification(category, flightOrBound.id, selectionsToStore);

          if (category === Category.PET) {
            petcIncompatibility[flightOrBound.id]?.forEach(([flightId, travelerId]) => {
              this.store$.dispatch(
                SeatMapActions.setSeatForFlight({
                  flightId,
                  travelerId,
                  seat: {
                    seatNumber: null,
                  },
                })
              );
            });
          }

          this.store$.dispatch(
            ServicesActions.setSelectionsForFragment({
              category: category,
              fragmentId: flightOrBound.id,
              selections: selectionsToStore,
            })
          );
        })
    );
  }

  private checkAndQueueToasterNotification(
    category: Category,
    flightOrBoundId: string,
    selections: SelectionsPerTraveler<TravelerService[]>
  ): void {
    if (category !== Category.MEAL) {
      return;
    }

    const nextHasSpecialDiet: Record<string, boolean> = Object.entries(selections).reduce(
      (all, [key, selectionsForTraveler]) => {
        all[key] = selectionsForTraveler.some((v) => v.category === SubCategory.SPECIAL_DIET);
        return all;
      },
      {}
    );
    const currentSelection: SelectionsPerTraveler<TravelerService | TravelerService[]> = snapshot(
      this.store$.pipe(serviceSelectionsForFragment(flightOrBoundId, this.category))
    );
    const currentHasSpecialDiet: Record<string, boolean> = Object.entries(currentSelection).reduce(
      (all, [key, travelerSelection]: [string, TravelerService | TravelerService[]]) => {
        const selectionsForTraveler = Array.isArray(travelerSelection) ? travelerSelection : [travelerSelection];
        all[key] = selectionsForTraveler.some((v) => v.category === SubCategory.SPECIAL_DIET);
        return all;
      },
      {}
    );

    const showSpecialDietSelected = Object.entries(nextHasSpecialDiet).some(
      ([key, value]) => value && !currentHasSpecialDiet[key]
    );
    const showSpecialDietUnselected = Object.entries(currentHasSpecialDiet).some(
      ([key, value]) => value && !nextHasSpecialDiet[key]
    );

    if (showSpecialDietSelected) {
      this.toasterService.addMessageToQueue({
        id: COMMON_TOASTER_ID,
        contentKey: 'ancillaries.specialDiet.toastMessages.selectedSpecialMeal',
      });
    }

    if (showSpecialDietUnselected) {
      this.toasterService.addMessageToQueue({
        id: COMMON_TOASTER_ID,
        contentKey: 'ancillaries.specialDiet.toastMessages.unSelectedSpecialMeal',
      });
    }
  }

  private isRadioDisplay(category: Category = this.category): boolean {
    return [Category.WIFI, Category.MEAL, Category.SEAT_WIFI].includes(category);
  }

  private setupTravelersAndServices(
    baseStream$: Observable<[Category, FinnairItineraryItemFlight | FinnairBoundItem]>,
    filteredTravelers$: Observable<ServicePassenger[]>
  ): void {
    const selectionsForFragmentAndCategory$ = baseStream$.pipe(
      switchMap(([category, flightOrBound]) => {
        return this.store$.pipe(
          serviceSelectionsForFragment<TravelerService | TravelerService[]>(flightOrBound.id, category)
        );
      })
    );

    const passengersWithSelectionAtInit = Object.values(snapshot(selectionsForFragmentAndCategory$) ?? {}).filter(
      hasTravelerService
    ).length;

    const remainingVariantQuotasForFragment$ = baseStream$.pipe(
      switchMap(([_category, flightOrBound]) =>
        this.remainingVariantQuotas$.pipe(map((remaining) => remaining?.[flightOrBound.id] ?? {}))
      ),
      distinctUntilChanged<VariantUnits>(equals),
      finShare()
    );

    const remainingCategoryQuotaForFragment$: Observable<number | undefined> = baseStream$.pipe(
      switchMap(([_category, flightOrBound]) =>
        this.remainingCategoryQuotas$.pipe(map((remaining) => remaining?.[flightOrBound.id]))
      ),
      distinctUntilChanged(),
      finShare()
    );

    this.setupCategoryQuotaNotification(
      baseStream$,
      filteredTravelers$,
      selectionsForFragmentAndCategory$,
      remainingCategoryQuotaForFragment$
    );

    this.travelersAndServices$ = combineLatest([
      this.services$,
      filteredTravelers$,
      this.includedServices$,
      this.combinedServices$,
      baseStream$,
    ]).pipe(
      map(([services, travelers, includedServices, combinedServices, [category, flightOrBound]]) => {
        if (Object.values(services ?? {}).length === 0) {
          return [];
        }

        // filteredTravelers$ re-emits whenever the travelers signal changes, so reading the raw
        // (infant-inclusive) list directly here stays in sync with the emission that triggered this map.
        const finnairPassengers = this.travelers();

        return travelers.map((traveler: ServicePassenger) => {
          const selection$: Observable<TravelerSelections> = this.form.controls[traveler.id]?.valueChanges.pipe(
            startWith(this.form.controls[traveler.id].getRawValue()),
            finShare()
          );

          const includedServicesForTravelerAndFragment = findServiceItemsForPassengerForFragment(
            category,
            includedServices,
            traveler.id,
            flightOrBound.id
          );

          const combinedGroupedServices = getGroupedServices(
            findServiceItemsForPassengerForFragment(
              this.category,
              combinedServices,
              traveler.id,
              flightOrBound.id,
              false
            ),
            getPassengerTierLevel(traveler.id, finnairPassengers)
          );

          const servicesToDisplay =
            includedServicesForTravelerAndFragment.length > 0 && this.isRadioDisplay()
              ? (services?.[traveler.id] ?? []).filter(
                  (s) =>
                    !!includedServicesForTravelerAndFragment.find(({ variant, totalPrice, subCategory }) => {
                      return (
                        variant === s.variant ||
                        (totalPrice?.amount === '0' && subCategory === SubCategory.SPECIAL_DIET)
                      );
                    })
                )
              : (services?.[traveler.id] ?? []);

          const quotaBearingVariants = quotaBearingVariantsOf(servicesToDisplay);
          const paidQuotaBearingUnits = paidUnitsOfVariants(
            includedServicesForTravelerAndFragment,
            quotaBearingVariants
          );

          const noneLeftForTraveler$ = combineLatest([remainingCategoryQuotaForFragment$, selection$]).pipe(
            map(
              ([remaining, selection]) =>
                remaining === 0 &&
                paidQuotaBearingUnits === 0 &&
                countOwnUnitsOfVariants(selection, quotaBearingVariants) === 0
            ),
            distinctUntilChanged(),
            finShare()
          );

          const extendedServices = this.getExtendedServices(
            traveler,
            servicesToDisplay,
            selection$,
            passengersWithSelectionAtInit,
            selectionsForFragmentAndCategory$,
            includedServicesForTravelerAndFragment,
            category,
            flightOrBound,
            remainingVariantQuotasForFragment$,
            remainingCategoryQuotaForFragment$,
            noneLeftForTraveler$
          );

          const selectedServices$: Observable<ExtendedService[]> = this.getSelectedServices(
            selection$,
            extendedServices
          );

          const filters = this.getFilters(extendedServices);
          const selectedFilter$ = new BehaviorSubject(
            filters.length === 0 ? 'all' : filters.length > 1 ? filters[0] : 'none'
          );
          return {
            traveler,
            filters,
            selectedFilter$,
            combinedGroupedServices,
            services: extendedServices,
            selectedServices$,
            hasServiceSelected$: this.getHasServiceSelected(
              selection$,
              includedServicesForTravelerAndFragment,
              category
            ),
            additionalInfoBlockUrl: this.getAdditionalInfoBlock(extendedServices, category, flightOrBound),
            additionalInfoBlockNotification: this.getAdditionalInfoBlockNotification(category, flightOrBound),
            noneLeftForTraveler$,
          } as TravelerServices;
        });
      }),
      finShare()
    );

    this.setupTierPricesMap();
  }

  private setupTierPricesMap(): void {
    this.travelerToServiceVariantTierPricesMap$ = combineLatest([
      this.travelersAndServices$,
      this.travelerProductQuantityChange$,
    ]).pipe(
      map(([travelersAndServices, _]) => {
        return travelersAndServices.reduce((travelerAndVariantsWithNextPriceMap, travelerAndServices) => {
          const variantsWithTierPricing = travelerAndServices.services
            .filter((service) => service.tieredPriceItems?.length > 0)
            .map((service) => service.variant);
          const variantsToTieredPricesMap = variantsWithTierPricing.reduce(
            (variantToTieredPricesMap, currentVariant) => {
              const alreadyBoughtServiceVariantsAmount =
                travelerAndServices.combinedGroupedServices
                  .filter((combinedServices) => combinedServices.key === 'confirmed')
                  .flatMap((combinedServices) => combinedServices.services)
                  .filter((service) => service.variant === currentVariant)?.length ?? 0;

              const variantServicesNumberInCartForCalculation =
                +this.form.controls[travelerAndServices.traveler.id].controls[currentVariant].value;

              const tieredPriceItems =
                travelerAndServices.services.find((service) => service.variant === currentVariant)?.tieredPriceItems ??
                [];

              const { accumulatedPrice, accumulatedOriginalPrice } =
                this.calculateCurrentTierPricedServiceAccumulatePrice(
                  tieredPriceItems,
                  variantServicesNumberInCartForCalculation,
                  alreadyBoughtServiceVariantsAmount
                );

              variantToTieredPricesMap.set(currentVariant, {
                accumulatedPrice,
                accumulatedOriginalPrice,
                currentProductPrice: this.findNextServiceVariantPrice(
                  tieredPriceItems,
                  alreadyBoughtServiceVariantsAmount + variantServicesNumberInCartForCalculation,
                  'totalPrice'
                ),
                currentProductPriceAsPoints: this.findNextServiceVariantPrice(
                  tieredPriceItems,
                  alreadyBoughtServiceVariantsAmount + variantServicesNumberInCartForCalculation,
                  'totalPoints'
                ),
              });
              return variantToTieredPricesMap;
            },
            new Map<string, ProductTierPrice>()
          );
          travelerAndVariantsWithNextPriceMap.set(travelerAndServices.traveler.id, variantsToTieredPricesMap);
          return travelerAndVariantsWithNextPriceMap;
        }, new Map<string, Map<string, ProductTierPrice>>());
      })
    );
  }

  private setupCategoryQuotaNotification(
    baseStream$: Observable<[Category, FinnairItineraryItemFlight | FinnairBoundItem]>,
    filteredTravelers$: Observable<ServicePassenger[]>,
    selectionsForFragmentAndCategory$: Observable<SelectionsPerTraveler<TravelerService | TravelerService[]>>,
    remainingCategoryQuotaForFragment$: Observable<number | undefined>
  ): void {
    const travelersWithoutSelection$ = combineLatest([
      filteredTravelers$,
      selectionsForFragmentAndCategory$,
      this.includedServices$,
      baseStream$,
    ]).pipe(
      map(
        ([travelers, selections, includedServices, [category, flightOrBound]]) =>
          travelers.filter((traveler) => {
            const hasSelection = hasTravelerService(selections?.[traveler.id]);
            const hasPurchased =
              findServiceItemsForPassengerForFragment(category, includedServices, traveler.id, flightOrBound.id)
                .length > 0;

            return !hasSelection && !hasPurchased;
          }).length
      ),
      distinctUntilChanged(),
      finShare()
    );

    this.categoryQuotaNotification$ = combineLatest([
      remainingCategoryQuotaForFragment$,
      travelersWithoutSelection$,
      filteredTravelers$,
      baseStream$,
    ]).pipe(
      map(([remaining, travelersWithoutSelection, travelers, [category]]) =>
        getCategoryQuotaNotification(
          getCategoryQuotaState(remaining, travelersWithoutSelection, travelers.length),
          category
        )
      ),
      distinctUntilChanged<QuotaNotification | undefined>(equals),
      finShare()
    );
  }

  private calculateCurrentTierPricedServiceAccumulatePrice(
    tieredPriceItems: FinnairServiceVariantItemV3[],
    currentServiceVariantNumberInCart: number,
    alreadyBoughtServiceVariantsAmount: number
  ): { accumulatedPrice: FinnairAmount; accumulatedOriginalPrice: FinnairAmount | undefined } {
    const hasOriginalPrice = tieredPriceItems.some((tier) => isPresent(tier.originalPrice));

    const { total, totalOriginal } = accumulateTieredPrice(
      tieredPriceItems.map(({ totalPrice, originalPrice, maxNumberItems }) => ({
        price: totalPrice,
        originalPrice,
        maxNumberItems,
      })),
      currentServiceVariantNumberInCart,
      alreadyBoughtServiceVariantsAmount
    );

    const shouldIncludeOriginalPrice = hasOriginalPrice && total.amount !== totalOriginal.amount;

    return {
      accumulatedPrice: total,
      ...(shouldIncludeOriginalPrice && { accumulatedOriginalPrice: totalOriginal }),
    };
  }

  private findNextServiceVariantPrice(
    tieredPriceItems: FinnairServiceVariantItemV3[],
    currentProductVariantsInCartRemainingCalculation: number,
    priceType: 'totalPrice' | 'totalPoints'
  ): FinnairAmount {
    const { finalPrice } = tieredPriceItems.reduce(
      (finalProductPrice, currentTieredPrice) => {
        if (finalProductPrice.remainingItems < 0) {
          return finalProductPrice;
        }
        const remainingItems = finalProductPrice.remainingItems - currentTieredPrice.maxNumberItems;
        return {
          remainingItems,
          finalPrice: currentTieredPrice[priceType],
        };
      },
      {
        remainingItems: currentProductVariantsInCartRemainingCalculation,
        finalPrice: tieredPriceItems[0][priceType],
      }
    );
    return finalPrice;
  }

  private getHasServiceSelected(
    selection$: Observable<TravelerSelections>,
    includedServicesForTravelerAndFragment: FinnairPassengerServiceSelectionItem[],
    category: Category
  ): Observable<boolean> {
    return selection$.pipe(
      map((selection) => {
        if (typeof selection['radio'] === 'string') {
          const alreadyPaid = !!includedServicesForTravelerAndFragment.find(
            (service) => service.variant === selection['radio']
          );
          return selection['radio'] !== CONFIG[category].notSelectedVariant || alreadyPaid;
        }
        return Object.keys(selection).some((variant) => {
          const alreadyPaid = !!includedServicesForTravelerAndFragment.find((service) => service.variant === variant);

          const travellerSelection = selection[variant];

          return (
            (typeof travellerSelection === 'number' ? travellerSelection > 0 : toBoolean(travellerSelection)) ||
            alreadyPaid
          );
        });
      })
    );
  }

  private getFilters(extendedServices: ExtendedService[]): SubCategoryFilter[] {
    if (this.category !== Category.MEAL) {
      return [];
    }
    const baseFilters = extendedServices.some((s) => s.subCategory === SubCategory.SPECIAL_DIET)
      ? extendedServices.flatMap((s) => s.subCategory).filter(unique)
      : [];

    return baseFilters.length > 1 ? [...baseFilters, 'all'] : baseFilters;
  }

  private getSelectedServices(
    selection$: Observable<TravelerSelections>,
    extendedServices: ExtendedService[]
  ): Observable<ExtendedService[]> {
    return selection$.pipe(
      map((selection) => {
        return extendedServices
          .filter((service) => {
            if (service.maxNumberItems > 1) {
              return Number(selection[service.variant]) > 0;
            }
            if (this.isRadioDisplay()) {
              return service.variant === selection['radio'] && !NO_VARIANTS.includes(selection['radio']);
            }
            return toBoolean(selection[service.variant]);
          })
          .map((service) => {
            if (typeof selection[service.variant] === 'number') {
              return {
                ...service,
                quantity: Number(selection[service.variant]),
                totalPrice: {
                  ...service.totalPrice,
                  amount: PreciseNumber.fromString(service.totalPrice.amount)
                    .multiply(PreciseNumber.fromString(String(selection[service.variant])))
                    .toString(),
                },
                ...(service.originalPrice && {
                  originalPrice: {
                    ...service.originalPrice,
                    amount: PreciseNumber.fromString(service.originalPrice.amount)
                      .multiply(PreciseNumber.fromString(String(selection[service.variant])))
                      .toString(),
                  },
                }),
              };
            }
            return { ...service, quantity: 1 };
          })
          .filter((service) => service.paidQuantity === 0 || +service.totalPrice?.amount === 0);
      })
    );
  }

  private getExtendedServices(
    traveler: ServicePassenger,
    services: ServiceCatalogService[],
    selection$: Observable<TravelerSelections>,
    passengersWithSelectionAtInit: number,
    selectionsForFragmentAndCategory$: Observable<SelectionsPerTraveler<TravelerService | TravelerService[]>>,
    includedServicesForTravelerAndFragment: FinnairPassengerServiceSelectionItem[],
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem,
    remainingVariantQuotasForFragment$: Observable<VariantUnits>,
    remainingCategoryQuotaForFragment$: Observable<number | undefined>,
    noneLeftForTraveler$: Observable<boolean>
  ): ExtendedService[] {
    const passengersWithSelection$ = selectionsForFragmentAndCategory$.pipe(
      map((selections) => Object.values(selections).filter(hasTravelerService).length),
      finShare()
    );

    const travelerHasSelection$ = selectionsForFragmentAndCategory$.pipe(
      map((selections) => hasTravelerService(selections?.[traveler.id])),
      distinctUntilChanged(),
      finShare()
    );

    const quotaTrackedForFragment$ = combineLatest([
      remainingVariantQuotasForFragment$,
      remainingCategoryQuotaForFragment$,
    ]).pipe(
      map(([variantLevel, categoryLevel]) => !isEmptyObject(variantLevel) || isPresent(categoryLevel)),
      distinctUntilChanged(),
      finShare()
    );

    return services
      .map((service: ServiceCatalogService) => {
        const paidQuantity =
          includedServicesForTravelerAndFragment.find((s) => s.variant === service.variant)?.quantity ?? 0;
        const selected$ = this.getIsSelectedStream(selection$, service, paidQuantity);
        const displayTotalPrice$ = selection$.pipe(
          map((selection) => {
            const count = service.maxNumberItems > 1 ? Number(selection[service.variant]) : 1;

            return {
              amount: PreciseNumber.fromString(service.totalPrice.amount)
                .multiply(PreciseNumber.from(count, 0))
                .toString(),
              currencyCode: service.totalPrice.currencyCode,
            };
          })
        );
        const inputType = this.getInputType(service);
        const remainingVariantQuota$ = remainingVariantQuotasForFragment$.pipe(
          map((remaining) => remaining[service.variant]),
          distinctUntilChanged(),
          finShare()
        );
        const ownUnits$ = selection$.pipe(
          map((selection) => Number(selection[service.variant] ?? 0)),
          distinctUntilChanged(),
          finShare()
        );

        const otherTravelersUnits$ = selectionsForFragmentAndCategory$.pipe(
          map((selections) =>
            Object.entries(selections)
              .filter(([travelerId]) => travelerId !== traveler.id)
              .reduce((sum, [, travelerService]) => {
                const selectedServices = Array.isArray(travelerService)
                  ? travelerService
                  : travelerService
                    ? [travelerService]
                    : [];

                return (
                  sum +
                  selectedServices
                    .filter((selectedItem) => selectedItem?.variant === service.variant)
                    .reduce((total, selectedItem) => total + (selectedItem.quantity ?? 1), 0)
                );
              }, 0)
          ),
          distinctUntilChanged(),
          finShare()
        );
        const paxLevelQuotaReached$ = this.getPaxLevelQuotaReached(service.variant, flightOrBound.id, traveler.id).pipe(
          finShare()
        );

        return {
          ...service,
          paidQuantity,
          displayTotalPrice$,
          selected$,
          inputType,
          passengerCountQuota$: combineLatest([
            selected$,
            passengersWithSelection$,
            quotaTrackedForFragment$,
            travelerHasSelection$,
          ]).pipe(
            map(([isSelected, passengersWithSelection, quotaTracked, travelerHasSelection]) =>
              quotaTracked
                ? { type: PassengerCountQuotaType.FREE, count: undefined }
                : calculatePassengerCountQuota(
                    passengersWithSelection,
                    service,
                    isSelected,
                    passengersWithSelectionAtInit,
                    travelerHasSelection
                  )
            ),
            finShare()
          ),
          remainingVariantQuota$,
          noneLeftForTraveler$: isKnownQuota(service.quota) ? noneLeftForTraveler$ : of(false),
          variantTakenByOtherPassenger$: combineLatest([remainingVariantQuota$, ownUnits$, otherTravelersUnits$]).pipe(
            map(([remaining, ownUnits, others]) => remaining === 0 && ownUnits === 0 && others > 0),
            distinctUntilChanged(),
            finShare()
          ),
          maxSelectableUnits$: combineLatest([ownUnits$, paxLevelQuotaReached$, remainingVariantQuota$]).pipe(
            map(([ownUnits, paxLevelQuotaReached, remaining]) =>
              Math.min(
                paxLevelQuotaReached ? ownUnits : service.maxNumberItems - paidQuantity,
                isPresent(remaining) ? ownUnits + remaining : service.maxNumberItems - paidQuantity
              )
            ),
            distinctUntilChanged(),
            finShare()
          ),
          paxLevelQuotaReached$,
          globalId: `${traveler.id}-${category}-${flightOrBound.id}-${service.variant}`,
          buttonText$: selected$.pipe(
            switchMap((selected) => {
              return this.languageService.translateMultiple(
                [
                  `ancillaries.${service.category}.${service.variant}.${selected ? 'selected' : 'select'}`,
                  `ancillaries.${service.category}.${selected ? 'selected' : 'select'}`,
                ],
                undefined,
                { logErrors: false }
              );
            }),
            map((translations) => translations[0] || translations[1])
          ),
        };
      })
      .filter((s) => s.isService)
      .sort((a, b) => (a.isHighlighted && !b.isHighlighted ? -1 : !a.isHighlighted && b.isHighlighted ? 1 : 0));
  }

  removeSelections(travelerId: string, flightOrBoundId: string): void {
    this.subscriptions.add(
      this.store$
        .pipe(safeSelect(serviceSelectionsForFragmentAndTraveler(flightOrBoundId, travelerId, this.category)), take(1))
        .subscribe((storeSelections: FinnairPassengerServiceSelectionItem[]) => {
          const seatBundlesInStore = storeSelections.filter((s) => isPresent(s.seatNumber));

          if (seatBundlesInStore.length > 0) {
            seatBundlesInStore.forEach(({ seatNumber }) => {
              this.store$.dispatch(
                SeatMapActions.setSeatForFlight({
                  flightId: flightOrBoundId,
                  travelerId,
                  seat: { seatNumber: seatNumber },
                })
              );
            });
          }
        })
    );

    if (this.form.controls[travelerId].controls['radio']) {
      this.form.controls[travelerId].controls['radio'].setValue('');
    }
  }

  private getPaxLevelQuotaReached(variant: string, fragmentId: string, travelerId: string) {
    return combineLatest([this.paxLevelQuota$, this.serviceQuantityMap$]).pipe(
      map(([paxLevelQuota, quantity]) => {
        if (!paxLevelQuota.variants.includes(variant)) {
          return false;
        }
        return quantity[fragmentId]?.[travelerId] >= paxLevelQuota.combinedQuota;
      }),
      startWith(false),
      distinctUntilChanged()
    );
  }

  private getInputType(service: ServiceCatalogService): ProductInputType {
    return (
      this.isRadioDisplay(service.category) ? 'radio' : service.maxNumberItems > 1 ? 'number' : 'checkbox'
    ) as ProductInputType;
  }

  private getIsSelectedStream(
    selection$: Observable<TravelerSelections>,
    service: ServiceCatalogService,
    paidQuantity: number
  ): Observable<boolean> {
    return selection$?.pipe(
      map((selection): boolean => {
        if (paidQuantity > 0 && (!selection['radio'] || selection['radio'] === service.variant)) {
          return true;
        }
        return this.isRadioDisplay(service.category)
          ? selection['radio'] === service.variant
          : toBoolean(selection[service.variant]);
      }),
      finShare()
    );
  }

  private departsFromHelsinki(flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem): boolean {
    return flightOrBound?.departure?.locationCode === 'HEL';
  }
}
